import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Toast, ToastStatus } from '../ui';

/**
 * The quiet "done" after a wait moved the wizard on by itself: one timed
 * toast at the top right of the step column. The provider sits above the
 * steps, so a step can announce what it finished and then navigate away —
 * the toast outlives the step that raised it. A newer toast replaces the
 * one showing; there is never a stack.
 */
export interface WizardToastInput {
  title: React.ReactNode;
  message?: React.ReactNode;
  status?: ToastStatus;
}

type Show = (toast: WizardToastInput) => void;

interface ToastState {
  show: Show;
  current: (WizardToastInput & { id: number }) | null;
  clear: (id: number) => void;
}

const noop: Show = () => undefined;
const WizardToastContext = createContext<ToastState>({ show: noop, current: null, clear: () => undefined });

export const WizardToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [current, setCurrent] = useState<ToastState['current']>(null);
  const show = useCallback<Show>((toast) => setCurrent({ ...toast, id: Date.now() }), []);
  const clear = useCallback((id: number) => setCurrent((now) => (now?.id === id ? null : now)), []);
  const value = useMemo(() => ({ show, current, clear }), [show, current, clear]);
  return <WizardToastContext.Provider value={value}>{children}</WizardToastContext.Provider>;
};

/** Raises the step's done toast; a no-op outside the wizard. */
export const useWizardToast = (): Show => useContext(WizardToastContext).show;

/** Where the toast floats — render inside a `relative` box over the step column. */
export const WizardToastOutlet: React.FC = () => {
  const { current, clear } = useContext(WizardToastContext);
  if (!current) return null;
  return (
    <div className="pointer-events-none absolute right-[24px] top-[20px] z-50 max-md:left-s5 max-md:right-s5">
      <Toast
        key={current.id}
        timed
        status={current.status ?? 'success'}
        title={current.title}
        message={current.message}
        onDone={() => clear(current.id)}
        className="max-md:w-full"
      />
    </div>
  );
};

export default WizardToastProvider;
